/**
 * The marking page's arithmetic: an assignment's counts, and what one row of an open assignment
 * is. Pure, so it can be tested without rendering anything.
 *
 * 🔴 ONE PAGE, THREE SOURCES, AND EACH ANSWERS ONE QUESTION (2026-10-03). Assignments, Grades
 * and Entry were three pages over the same submissions. Merged, the page reads:
 *   - `GET /cohort` — every grade this console holds, from its own database. Every count on the
 *     page comes from here, so no number waits on Google.
 *   - `GET /entry-queue` — the marks still to type into Classroom, from the database too.
 *   - `GET /assignments/{cw}/submissions` — what Classroom lists for ONE assignment, the one
 *     that is open. It is the only per-row Google call, and it is made for one at a time.
 * The open assignment joins the third to the first by `submission_id`.
 */
import type { CohortRow, EntryRow, SubmissionRow } from "../api";
import type { MarkingFilter } from "../routes";

/** ⚠️ EVERY COUNT CARRIES A SENTENCE. A number under a one-word label is not actionable
 *  until something says what a row in it means and what to do about it. */
export const STATUS_HINT: Record<string, string> = {
  READY: "measured and unflagged — release when you have read it",
  REVIEW: "the validator wants a human before this goes out",
  HELD: "the pipeline could not measure it — no score exists",
  RELEASED: "the student has been notified and can see the mark",
};

/** What a row under each filter is called — in the report's way back to the list its grade is in. */
export const FILTER_NOUN: Record<MarkingFilter, string> = {
  review: "in review", ready: "ready", held: "held", done: "released or entered",
  running: "running or queued",
};

/**
 * One assignment's counts, from the database alone.
 *
 * 🔴 AN ENTERED MARK IS DONE, LIKE A RELEASED ONE — the Overview's meaning (2026-10-03). Typing a
 * mark into Classroom by hand is how it reaches a student on coursework a teacher made in the
 * Classroom UI, which is every live assignment; the grade row stays READY because this console
 * never wrote to Google. Counted as `ready`, a fully hand-entered assignment read "ready 14 ·
 * released 0" under a hint saying to release them, while the Overview called the same fourteen
 * "released or entered".
 */
export interface Tally {
  graded: number;
  /** REVIEW — escalated and not reviewed, entered by hand or not: nobody has reviewed it. */
  review: number;
  /** READY and not entered by hand: measured, and still to go out. */
  ready: number;
  held: number;
  /** RELEASED — written to Classroom by this console. */
  released: number;
  /** Recorded as typed into Classroom by hand, whatever its status word. */
  entered: number;
  /** Released or entered — the student has the mark. Never both at once for one grade, because
   *  Entry lists only grades that were not released. */
  done: number;
}

export const blankTally = (): Tally =>
  ({ graded: 0, review: 0, ready: 0, held: 0, released: 0, entered: 0, done: 0 });

/** `(course_work_id, submission_id)` as one key — the server's own `entry.entered_keys`. A key on
 *  the submission alone would collapse two assignments for one student. */
export const pairKey = (cw: string, sid: string) => `${cw}\u0000${sid}`;

/** The marks recorded as typed into Classroom by hand. */
export function enteredKeys(entry: EntryRow[]): Set<string> {
  return new Set(entry.filter((e) => e.already_entered)
    .map((e) => pairKey(e.course_work_id, e.submission_id)));
}

export const isEnteredGrade = (keys: Set<string>,
                               g: { course_work_id: string; submission_id: string }): boolean =>
  keys.has(pairKey(g.course_work_id, g.submission_id));

/**
 * Every assignment's counts: `GET /cohort` grouped by `course_work_id`, plus the marks still to
 * type from `GET /entry-queue`.
 *
 * ⚠️ GROUPED HERE RATHER THAN READ FROM `GET /assignments`. That route's own `ours` is the same
 * arithmetic, but it answers only after a Classroom listing — so counts that came from it would
 * wait on Google, and vanish with it. These are the database's, and they sum to the cohort's
 * counts by construction because they are the same rows.
 */
export function tallies(grades: CohortRow[], entry: EntryRow[]): Map<string, Tally> {
  const out = new Map<string, Tally>();
  const of = (cw: string) => {
    let t = out.get(cw);
    if (!t) { t = blankTally(); out.set(cw, t); }
    return t;
  };
  const entered = enteredKeys(entry);
  for (const g of grades) {
    const t = of(g.course_work_id);
    const typed = isEnteredGrade(entered, g);
    t.graded += 1;
    if (g.status === "REVIEW") t.review += 1;
    else if (g.status === "READY" && !typed) t.ready += 1;
    else if (g.status === "HELD") t.held += 1;
    else if (g.status === "RELEASED") t.released += 1;
    if (typed) t.entered += 1;
  }
  for (const t of out.values()) t.done = t.released + t.entered;
  return out;
}

/** One row of an open assignment: what Classroom lists, joined to what the database holds. */
export interface MarkRow {
  submission_id: string;
  /** Classroom's listing — null when the database holds a grade Classroom no longer lists. */
  sub: SubmissionRow | null;
  /** The database's grade — null when nothing is graded here yet. */
  grade: CohortRow | null;
  /** Entry's row, when the mark could be typed into Classroom by hand. */
  entry: EntryRow | null;
  /** Whether `GET /cohort` had answered when this row was joined. Until it has, the listing's
   *  own mark stands in; once it has, a row it holds no grade for has none. */
  gradesRead: boolean;
}

/**
 * Classroom's listing for one assignment, joined to the database's grades by `submission_id`.
 *
 * 🔴 A GRADE CLASSROOM NO LONGER LISTS IS STILL A ROW. A student who deleted their hand-in, or a
 * submission Google stopped returning, leaves a grade this console paid for and holds — the old
 * Grades page showed it, and dropping it here would hide a paid result. It comes after the
 * listed rows, with `sub: null`, and the page says so on the row.
 */
export function joinSubmissions(listed: SubmissionRow[], grades: CohortRow[] | null,
                                entry: EntryRow[], courseWorkId: string): MarkRow[] {
  // ⚠️ `null` IS "NOT READ YET", NEVER "NONE". An empty list from `/cohort` says nothing here is
  // graded; a read still in flight says nothing at all.
  const gradesRead = grades !== null;
  const mine = (grades ?? []).filter((g) => g.course_work_id === courseWorkId);
  const bySid = new Map(mine.map((g) => [g.submission_id, g]));
  const entryBySid = new Map(entry.filter((e) => e.course_work_id === courseWorkId)
    .map((e) => [e.submission_id, e]));
  const seen = new Set<string>();
  const out: MarkRow[] = listed.map((s) => {
    seen.add(s.submission_id);
    return { submission_id: s.submission_id, sub: s, grade: bySid.get(s.submission_id) ?? null,
             entry: entryBySid.get(s.submission_id) ?? null, gradesRead };
  });
  for (const g of mine) {
    if (!seen.has(g.submission_id)) {
      out.push({ submission_id: g.submission_id, sub: null, grade: g,
                 entry: entryBySid.get(g.submission_id) ?? null, gradesRead });
    }
  }
  return out;
}

/** The mark one row shows, and where it came from. */
export interface Mark {
  /** The mark that goes out — the reviewed one where a review is in force. */
  points: number | null; max_points: number | null;
  /** What the agent gave, whatever a review later decided. */
  agent_points: number | null;
  status: string;
  reviewed_by: string | null;
  language: string;
}

/**
 * The mark a row shows: the database grade, which is also what the filters show for it.
 *
 * ⚠️ `sub.ours` ONLY BEFORE `GET /cohort` HAS ANSWERED. It is the same database row read by
 * another route, and it carries no reviewer and no language, so those read as absent rather than
 * as "not reviewed".
 * 🔴 NEVER ONCE `/cohort` HAS ANSWERED WITHOUT THE GRADE (fixed 2026-10-03). The fallback used to
 * apply whenever the grade was missing, whichever read was newer — so after a Binder change
 * archived every grade on an assignment, its open rows went on showing the archived marks as
 * READY, with report links, under counts that read 0. `/cohort` leaves archived grades out on
 * purpose, so its silence about a submission is an answer.
 */
export function markOf(r: MarkRow): Mark | null {
  if (r.grade) {
    return { points: r.grade.points, max_points: r.grade.max_points,
             agent_points: r.grade.agent_points, status: r.grade.status,
             reviewed_by: r.grade.reviewed_by,
             language: r.grade.language_label || r.grade.language };
  }
  if (r.gradesRead) return null;
  const o = r.sub?.ours;
  if (!o) return null;
  return { points: o.points, max_points: o.max_points, agent_points: o.agent_points,
           status: o.status, reviewed_by: null, language: "" };
}

/** Typed into Classroom by hand, as this console recorded it. */
export const isEntered = (r: MarkRow): boolean => Boolean(r.entry?.already_entered);

/** Ready: measured, its status word READY — a reviewed grade reads READY — and nobody has
 *  recorded it as typed into Classroom. The Ready filter's own rule, for one assignment's row. */
export const isReadyRow = (r: MarkRow): boolean => markOf(r)?.status === "READY" && !isEntered(r);

/**
 * Parked: in the queue for a reason that needs somebody — never `queued` (waiting its turn) and
 * never `dispatched` (a run in flight).
 *
 * ⚠️ THE OVERVIEW'S DEFINITION (`overview.py`, `parked_by_cw`), which an assignment's own "N
 * parked" is read from. The row order and the parked filter once counted `dispatched` too, so
 * with a run in flight the header said nothing was parked and the chip under it said one was.
 */
export const isParked = (q: SubmissionRow["queue"] | undefined): boolean =>
  Boolean(q && q.reason !== "queued" && q.reason !== "dispatched");

/**
 * 🔴 WORK FIRST: what a person has to do something about, then what is done. The old default's
 * comment said this and its code sorted by name, so the order was alphabetical while the source
 * claimed otherwise.
 */
export function workRank(r: MarkRow): number {
  const m = markOf(r);
  if (m?.status === "REVIEW") return 0;
  if (isReadyRow(r)) return 1;
  if (isParked(r.sub?.queue)) return 2;
  if (!m && r.sub?.runnable) return 3;
  if (m?.status === "HELD") return 4;
  if (m?.status === "READY") return 5;
  if (m?.status === "RELEASED") return 6;
  return 7;
}
